import type { WorkbookPivotSheet } from "@erp/shared-types";
import { missingInput } from "../missingInput";
import type { SheetKindAdapter } from "../sheetKinds";
import { pivotResultColumns, pivotStaticColumns } from "./layout";
import { pivotDeclaredOutput, planPivot } from "./plan";

/** The kind that summarizes its input's rows as a spreadsheet pivot table does. */
export const pivotSheetKind: SheetKindAdapter<WorkbookPivotSheet> = {
  input: (sheet) => sheet.inputId,
  // A pivot reads only its input.
  reads: (sheet) => [sheet.inputId],
  missingReads: (sheet, ids) => missingInput(sheet.id, sheet.inputId, ids),
  resultColumns: (sheet, columnNames) =>
    columnNames
      ? pivotResultColumns(sheet.pivot, columnNames)
      : pivotStaticColumns(sheet.pivot).columns,
  // Unpinned Column keys name value columns (Numbers) only its data has.
  dataNamedColumnType: (sheet) =>
    pivotStaticColumns(sheet.pivot).dynamic ? "decimal" : undefined,
  plan: (sheet, { schemas }) => {
    const { stages, diagnostics } = planPivot(
      sheet,
      schemas.get(sheet.inputId),
    );
    return {
      plan: {
        sheetId: sheet.id,
        output: pivotDeclaredOutput(sheet),
        stages: null,
        pivot: stages,
      },
      diagnostics,
    };
  },
  // A pivot's whole spec is Compute-relevant: Value names name its columns.
  fingerprintInput: (sheet) => [sheet.inputId, ["pivot", sheet.pivot]],
  // Row and Column fields name their output columns; a date-grouped one is changed.
  lineage: (sheet) => {
    const { pivot, inputId } = sheet;
    const groups = [...pivot.rows, ...pivot.columns];
    return {
      fields: new Map(
        pivot.rows
          .filter((group) => !group.dateGroup)
          .map((group) => [
            group.field.toLowerCase(),
            { tableId: inputId, column: group.field },
          ]),
      ),
      lookups: [],
      reads: [
        ...groups.map((group) => group.field),
        ...pivot.values.map((value) => value.field),
        ...pivot.filters.map((filter) => filter.column),
      ].map((column) => ({ tableId: inputId, column })),
    };
  },
  partName: (sheet, location) => {
    if (location.part !== "pivot") return undefined;
    const field = sheet.pivot[location.area][location.index];
    return field && ("field" in field ? field.field : field.column);
  },
  // Its Row, Column, Value and Filter fields, of its input; all of it until planned.
  columnReads: (sheet, { plan }) => [
    {
      tableId: sheet.inputId,
      columns: plan?.pivot
        ? [
            ...sheet.pivot.rows.map((group) => group.field),
            ...sheet.pivot.columns.map((group) => group.field),
            ...sheet.pivot.values.map((value) => value.field),
            ...sheet.pivot.filters.map((filter) => filter.column),
          ]
        : "all",
    },
  ],
  mapFormulas: (sheet) => sheet,
  presentationFormats: () => undefined,
};
